/**
 * Waiting list form functionality
 * Handles waiting-list signup submission via AJAX
 */

(function() {
    'use strict';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    function getErrorElement(input) {
        if (!input) {
            return null;
        }

        const describedBy = input.getAttribute('aria-describedby');
        return describedBy ? input.form.querySelector('#' + describedBy) : null;
    }

    function initForm(form) {
        const emailInput = form.querySelector('input[type="email"]');
        const consentCheckbox = form.querySelector('input[type="checkbox"]');
        const submitButton = form.querySelector('button[type="submit"]');
        const successMessage = form.querySelector('.waiting-list-form__message--success');
        const errorMessage = form.querySelector('.waiting-list-form__message--error');
        const defaultErrorText = errorMessage ? errorMessage.textContent : '';
        const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!emailInput || !submitButton) {
            return;
        }

        // Native constraint validation stays on in markup so the form still works if this
        // script never loads. Once JS is confirmed running, hand validation fully to our
        // custom UI so users don't see a native bubble AND our error text at the same time.
        form.noValidate = true;

        const emailError = getErrorElement(emailInput);
        const consentError = getErrorElement(consentCheckbox);

        function resetErrors() {
            emailInput.classList.remove('waiting-list-form__input--error');
            emailInput.setAttribute('aria-invalid', 'false');
            if (consentCheckbox) {
                consentCheckbox.classList.remove('waiting-list-form__checkbox--error');
                consentCheckbox.setAttribute('aria-invalid', 'false');
            }
            if (emailError) {
                emailError.style.display = 'none';
            }
            if (consentError) {
                consentError.style.display = 'none';
            }
            if (successMessage) {
                successMessage.style.display = 'none';
            }
            if (errorMessage) {
                errorMessage.style.display = 'none';
            }
        }

        function showFieldError(input, errorElement, message) {
            input.classList.add('waiting-list-form__input--error');
            input.setAttribute('aria-invalid', 'true');
            if (errorElement) {
                errorElement.textContent = message;
                errorElement.style.display = 'block';
            }
        }

        form.addEventListener('submit', function(event) {
            event.preventDefault();

            resetErrors();
            let isValid = true;
            let firstInvalid = null;

            const requiredFieldText = window.engeto_waiting_list_form_params?.translation_field_required || 'Toto pole je povinné.';
            const invalidEmailText = window.engeto_waiting_list_form_params?.translation_invalid_email || 'Zadej prosím validní e-mail.';

            const emailValue = emailInput.value.trim();
            if (!emailValue) {
                showFieldError(emailInput, emailError, requiredFieldText);
                isValid = false;
                firstInvalid = firstInvalid || emailInput;
            } else if (!emailRegex.test(emailValue)) {
                showFieldError(emailInput, emailError, invalidEmailText);
                isValid = false;
                firstInvalid = firstInvalid || emailInput;
            }

            if (!consentCheckbox || !consentCheckbox.checked) {
                if (consentCheckbox) {
                    consentCheckbox.classList.add('waiting-list-form__checkbox--error');
                    consentCheckbox.setAttribute('aria-invalid', 'true');
                }
                if (consentError) {
                    consentError.textContent = requiredFieldText;
                    consentError.style.display = 'block';
                }
                isValid = false;
                firstInvalid = firstInvalid || consentCheckbox;
            }

            if (!isValid) {
                if (firstInvalid) {
                    firstInvalid.focus();
                }
                return;
            }

            submitButton.disabled = true;
            submitButton.setAttribute('aria-busy', 'true');
            const originalButtonText = submitButton.textContent;
            submitButton.textContent = window.engeto_waiting_list_form_params?.translation_sending || 'Odesílám...';

            const nonceField = form.querySelector('input[name="nonce"]');
            const nonce = nonceField ? nonceField.value : '';
            const honeypotField = form.querySelector('input[name="engeto_hp"]');

            const data = new FormData();
            data.append('action', 'waiting_list_form');
            data.append('nonce', nonce);
            data.append('email', emailValue);
            data.append('consent', consentCheckbox && consentCheckbox.checked ? '1' : '0');
            data.append('engeto_hp', honeypotField ? honeypotField.value : '');

            let ajaxUrl = window.engeto_waiting_list_form_params?.ajaxurl;
            if (!ajaxUrl) {
                ajaxUrl = window.engeto_params?.ajaxurl;
            }
            if (!ajaxUrl) {
                const protocol = window.location.protocol;
                const host = window.location.host;
                ajaxUrl = protocol + '//' + host + '/wp-admin/admin-ajax.php';
            }

            fetch(ajaxUrl, {
                method: 'POST',
                credentials: 'same-origin',
                body: data
            })
            .then(response => {
                return response.text().then(text => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    if (text === '0' || text.trim() === '0') {
                        throw new Error('AJAX handler error');
                    }
                    try {
                        return JSON.parse(text);
                    } catch (error) {
                        throw new Error('Invalid response');
                    }
                });
            })
            .then(response => {
                if (response.valid) {
                    form.reset();
                    emailInput.setAttribute('aria-invalid', 'false');
                    if (consentCheckbox) {
                        consentCheckbox.setAttribute('aria-invalid', 'false');
                    }
                    if (successMessage) {
                        successMessage.style.display = 'block';
                        successMessage.setAttribute('tabindex', '-1');
                        successMessage.focus();
                        successMessage.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'nearest' });
                    }

                    if (typeof dataLayer !== 'undefined') {
                        dataLayer.push({ event: 'waitingListSubscription' });
                    }
                } else {
                    if (errorMessage) {
                        errorMessage.textContent = response.message || defaultErrorText;
                        errorMessage.style.display = 'block';
                        errorMessage.setAttribute('tabindex', '-1');
                        errorMessage.focus();
                    }
                    if (emailInput.classList.contains('waiting-list-form__input--error')) {
                        emailInput.focus();
                    } else if (consentCheckbox && consentCheckbox.classList.contains('waiting-list-form__checkbox--error')) {
                        consentCheckbox.focus();
                    }
                }
                submitButton.disabled = false;
                submitButton.removeAttribute('aria-busy');
                submitButton.textContent = originalButtonText;
            })
            .catch(() => {
                if (errorMessage) {
                    errorMessage.textContent = defaultErrorText;
                    errorMessage.style.display = 'block';
                    errorMessage.setAttribute('tabindex', '-1');
                    errorMessage.focus();
                }
                submitButton.disabled = false;
                submitButton.removeAttribute('aria-busy');
                submitButton.textContent = originalButtonText;
            });
        });
    }

    function initWaitingListForms() {
        const forms = document.querySelectorAll('.waiting-list-form__form');
        forms.forEach(form => {
            if (form.dataset.waitingListFormApplied === '1') {
                return;
            }
            form.dataset.waitingListFormApplied = '1';
            initForm(form);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initWaitingListForms);
    } else {
        initWaitingListForms();
    }
})();
